"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.measureScrollMapMarkers = void 0;
const slate_1 = require("slate");
const slate_react_1 = require("slate-react");
const embed_1 = require("./embed");
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const isElementNode = (node) => {
    return !!node && typeof node === 'object' && 'type' in node && 'children' in node;
};
const getChecklistProgress = (element) => {
    const items = element.children;
    return {
        checkedCount: items.filter((item) => !!item.checked).length,
        totalCount: items.length,
    };
};
const getMarkerHeight = (descriptor, blockHeight, scrollHeight, railHeight) => {
    if (!descriptor.proportional || railHeight <= 0 || scrollHeight <= 0)
        return descriptor.height;
    const scaled = Math.round((Math.max(blockHeight, 1) / scrollHeight) * railHeight);
    return Math.max(descriptor.height, scaled);
};
const isMediaMime = (mime) => /^image\//i.test(mime) || /^video\//i.test(mime) || /^audio\//i.test(mime);
const describeScrollMapElement = (element, light, getThing) => {
    switch (element.type) {
        case 'title':
            return { el: element, color: light ? '#234' : '#f8fafc', height: 5, variant: 'wide' };
        case 'subtitle':
            return { el: element, color: light ? '#789' : '#94a3b8', height: 3, variant: 'wide' };
        case 'h1':
            return { el: element, color: light ? '#345' : '#e5e7eb', height: 4, variant: 'wide' };
        case 'h2':
            return { el: element, color: light ? '#456' : '#cbd5e1', height: 3, variant: 'wide' };
        case 'h3':
            return { el: element, color: light ? '#678' : '#94a3b8', height: 2, variant: 'wide' };
        case 'h4':
        case 'h5':
        case 'h6':
            return { el: element, color: light ? '#89a' : '#64748b', height: 2, variant: 'wide' };
        case 'blockquote':
            return {
                el: element,
                color: light ? 'rgba(194,120,24,.6)' : 'rgba(245,182,84,.5)',
                height: 3,
                proportional: true,
            };
        case 'callout':
            return {
                el: element,
                color: element.color?.trim() || (light ? 'rgba(59,130,246,.6)' : 'rgba(96,165,250,.55)'),
                height: 3,
                proportional: true,
            };
        case 'code-block':
            return {
                el: element,
                color: light ? 'rgba(54,155,55,.45)' : 'rgba(120,195,125,.45)',
                height: 4,
                proportional: true,
            };
        case 'pre':
            return {
                el: element,
                color: light ? 'rgba(100,116,139,.5)' : 'rgba(148,163,184,.5)',
                height: 3,
                proportional: true,
            };
        case 'embed':
            return {
                el: element,
                color: light ? 'rgba(168,85,247,.6)' : 'rgba(196,181,253,.5)',
                height: 4,
                proportional: true,
            };
        case 'math':
            return {
                el: element,
                color: light ? 'rgba(99,102,241,.6)' : 'rgba(165,180,252,.55)',
                height: 4,
                proportional: true,
            };
        case 'ul':
            return {
                el: element,
                color: light ? 'rgba(27,184,212,.6)' : 'rgba(96,199,250,.5)',
                height: 3,
                proportional: true,
            };
        case 'ol':
            return {
                el: element,
                color: light ? 'rgba(13,107,148,.6)' : 'rgba(45,167,212,.5)',
                height: 3,
                proportional: true,
            };
        case 'stepper':
            return {
                el: element,
                color: light ? 'rgba(75,110,175,.6)' : 'rgba(130,165,225,.5)',
                height: 3,
                proportional: true,
            };
        case 'checklist': {
            const { checkedCount, totalCount } = getChecklistProgress(element);
            const complete = totalCount > 0 && checkedCount === totalCount;
            const started = checkedCount > 0 && checkedCount < totalCount;
            return {
                el: element,
                color: complete
                    ? light
                        ? 'rgba(47,143,53,.65)'
                        : 'rgba(110,200,125,.55)'
                    : started
                        ? light
                            ? 'rgba(200,150,40,.6)'
                            : 'rgba(220,180,55,.5)'
                        : light
                            ? 'rgba(220,38,38,.6)'
                            : 'rgba(248,113,113,.5)',
                height: 3,
                proportional: true,
            };
        }
        case 'file': {
            const thingId = element['@thing'];
            const thing = thingId && getThing ? getThing(thingId) : undefined;
            const isMedia = !!thing && isMediaMime(thing.mimeType || '');
            return {
                el: element,
                color: isMedia
                    ? light
                        ? 'rgba(190,90,210,.55)'
                        : 'rgba(205,150,225,.5)'
                    : light
                        ? 'rgba(115,115,115,.55)'
                        : 'rgba(163,163,163,.5)',
                height: 4,
                proportional: true,
            };
        }
        default: {
            const type = element.type;
            if (type === 'table') {
                return {
                    el: element,
                    color: light ? 'rgba(34,140,120,.6)' : 'rgba(110,195,175,.5)',
                    height: 4,
                    proportional: true,
                };
            }
            return null;
        }
    }
};
const getDomRangeRect = (range) => {
    const rect = range.getBoundingClientRect();
    if (rect.height > 0 || rect.width > 0)
        return rect;
    return range.getClientRects()[0] ?? null;
};
const getFocusedDomSelectionRange = (reactEditor) => {
    if (!slate_react_1.ReactEditor.isFocused(reactEditor))
        return null;
    const domSelection = slate_react_1.ReactEditor.getWindow(reactEditor).getSelection();
    if (!domSelection || domSelection.rangeCount <= 0)
        return null;
    const anchorNode = domSelection.anchorNode;
    const focusNode = domSelection.focusNode;
    if (!anchorNode || !focusNode)
        return null;
    if (!slate_react_1.ReactEditor.hasDOMNode(reactEditor, anchorNode) || !slate_react_1.ReactEditor.hasDOMNode(reactEditor, focusNode))
        return null;
    return domSelection.getRangeAt(0);
};
const getScrollMapSelectionRect = (reactEditor, light, getThing) => {
    const domSelectionRange = getFocusedDomSelectionRange(reactEditor);
    const activeEmbedEntry = (0, embed_1.getActiveEmbedEntry)(reactEditor);
    if (activeEmbedEntry && domSelectionRange) {
        const [element] = activeEmbedEntry;
        const descriptor = describeScrollMapElement(element, light, getThing);
        try {
            const domNode = slate_react_1.ReactEditor.toDOMNode(reactEditor, element);
            if (domNode instanceof HTMLElement) {
                return {
                    rect: domNode.getBoundingClientRect(),
                    proportional: descriptor?.proportional ?? true,
                };
            }
        }
        catch {
            // Slate may briefly invalidate the selected void node while reconciling changes.
        }
    }
    if (domSelectionRange) {
        const rect = getDomRangeRect(domSelectionRange);
        if (!rect)
            return null;
        return {
            rect,
            proportional: !domSelectionRange.collapsed,
        };
    }
    const selection = reactEditor.selection;
    if (!selection)
        return null;
    if (!slate_react_1.ReactEditor.isFocused(reactEditor) || !slate_react_1.ReactEditor.hasRange(reactEditor, selection))
        return null;
    try {
        const domRange = slate_react_1.ReactEditor.toDOMRange(reactEditor, selection);
        const rect = getDomRangeRect(domRange);
        if (!rect)
            return null;
        return {
            rect,
            proportional: !slate_1.Range.isCollapsed(selection),
        };
    }
    catch {
        return null;
    }
};
const measureScrollMapSelectionMarker = (reactEditor, viewportEl, viewportRect, scrollHeight, railHeight, light, getThing) => {
    const selection = reactEditor.selection;
    if (!selection)
        return null;
    const selectionRect = getScrollMapSelectionRect(reactEditor, light, getThing);
    if (!selectionRect)
        return null;
    const descriptor = {
        color: '#07f',
        height: 4,
        proportional: selectionRect.proportional,
        variant: 'selection',
    };
    const top = selectionRect.rect.top - viewportRect.top + viewportEl.scrollTop;
    return {
        ...descriptor,
        key: 'selection',
        color: 'transparent',
        position: clamp(top / scrollHeight, 0, 1),
        height: getMarkerHeight(descriptor, selectionRect.rect.height, scrollHeight, railHeight),
    };
};
const measureScrollMapMarkers = (editor, viewportEl, scrollHeight, railHeight, light, getThing) => {
    if (scrollHeight <= viewportEl.clientHeight || scrollHeight <= 0)
        return [];
    const markers = [];
    const reactEditor = editor;
    const viewportRect = viewportEl.getBoundingClientRect();
    editor.children.forEach((node, index) => {
        if (!isElementNode(node))
            return;
        const descriptor = describeScrollMapElement(node, light, getThing);
        if (!descriptor)
            return;
        try {
            const domNode = slate_react_1.ReactEditor.toDOMNode(reactEditor, node);
            if (!(domNode instanceof HTMLElement))
                return;
            const rect = domNode.getBoundingClientRect();
            const top = rect.top - viewportRect.top + viewportEl.scrollTop;
            markers.push({
                ...descriptor,
                key: index + ':' + node.type,
                position: clamp(top / scrollHeight, 0, 1),
                height: getMarkerHeight(descriptor, rect.height, scrollHeight, railHeight),
            });
        }
        catch {
            // Slate may briefly unmount/remount nodes while reconciling changes.
        }
    });
    const selectionMarker = measureScrollMapSelectionMarker(reactEditor, viewportEl, viewportRect, scrollHeight, railHeight, light, getThing);
    if (selectionMarker)
        markers.push(selectionMarker);
    return markers;
};
exports.measureScrollMapMarkers = measureScrollMapMarkers;
